<template>
  <Transition name="modal-fade">
    <div class="push-modal-overlay" v-if="visible" @click.self="dismiss">
      <div class="push-modal" role="dialog" aria-modal="true" aria-labelledby="push-modal-title">
        
        <div class="push-icon-wrapper">
          <div class="push-icon-ring">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <h2 id="push-modal-title">Benachrichtigungen aktivieren</h2>
        <p>Erhalte eine Nachricht, wenn jemand dein Profil liked oder dir schreibt – und verpasse keine neuen Matches!</p>

        <div class="push-modal-actions">
          <button class="push-activate-btn" @click="activate" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            <span v-else>Aktivieren</span>
          </button>
          <button class="push-dismiss-btn" @click="dismiss">Später</button>
        </div>

        <p class="push-hint" v-if="denied">
          Du hast Benachrichtigungen blockiert. Bitte erlaube sie in den Browsereinstellungen.
        </p>
      </div>
    </div>
  </Transition>
</template>

<script>
import registerPushWorker from '@/registerServiceWorker';

export default {
  name: 'PushPermissionModal',
  emits: ['activated', 'dismissed'],
  data() {
    return {
      visible: false,
      loading: false,
      denied: false,
    };
  },
  methods: {
    show() {
      // Don't show if push is not supported or already denied
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      if (Notification.permission === 'denied') return;
      this.visible = true;
    },
    dismiss() {
      this.visible = false;
      // Remember dismissal for this session so we don't annoy the user on every navigation
      sessionStorage.setItem('push_modal_dismissed', '1');
      this.$emit('dismissed');
    },
    async activate() {
      if (Notification.permission === 'denied') {
        this.denied = true;
        return;
      }

      this.loading = true;
      const success = await registerPushWorker();
      this.loading = false;

      if (success) {
        this.visible = false;
        this.$emit('activated');
      } else if (Notification.permission === 'denied') {
        this.denied = true;
      }
    },
  },
};
</script>

<style scoped>
.push-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  padding: 0 0 env(safe-area-inset-bottom, 0) 0;
}

.push-modal {
  background: white;
  border-radius: 28px 28px 0 0;
  padding: 32px 28px 40px;
  width: 100%;
  max-width: 480px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.15);
  animation: slide-up 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

.push-icon-wrapper {
  margin-bottom: 4px;
}

.push-icon-ring {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007AFF20, #007AFF10);
  border: 2px solid #007AFF30;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007AFF;
  animation: ring-pulse 2.5s ease-in-out infinite;
}

@keyframes ring-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 122, 255, 0.3); }
  50%       { box-shadow: 0 0 0 10px rgba(0, 122, 255, 0); }
}

h2 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1D1D1F;
  margin: 0;
}

p {
  font-size: 0.95rem;
  color: #6E6E73;
  line-height: 1.5;
  margin: 0;
  max-width: 320px;
}

.push-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 8px;
}

.push-activate-btn {
  width: 100%;
  padding: 16px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  font-family: var(--font, system-ui);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  transition: background 0.2s, transform 0.1s;
}

.push-activate-btn:active:not(:disabled) {
  background: #0066d6;
  transform: scale(0.98);
}

.push-activate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.push-dismiss-btn {
  width: 100%;
  padding: 14px;
  background: transparent;
  color: #6E6E73;
  border: none;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: var(--font, system-ui);
  cursor: pointer;
  transition: color 0.2s;
}

.push-dismiss-btn:active {
  color: #1D1D1F;
}

.push-hint {
  font-size: 0.82rem;
  color: #FF3B30;
  margin-top: 4px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
